/* v8 ignore start */
// Types for font data
type TFontSizeData = {
  fontSize: number;
  width: number;
  height: number;
};

type TFontTypeData = {
  [key in "downcase" | "upcase" | "space"]: TFontSizeData[];
};

type TFontData = {
  [key: string]: TFontTypeData;
};

export class TextUtils {
  private static readonly ALPHABET_CHARACTER_LENGTH = 26; // upper and lower case letters, and space
  private static readonly INCREASE_WIDTH_RATE = 1.07;
  private static readonly AVAILABLE_TYPES = ["upcase", "downcase", "space"] as const;

  private static readonly FONT_DATA: TFontData = {
    "Proxima Nova": {
      downcase: [
        {fontSize: 8, width: 106, height: 9},
        {fontSize: 288, width: 3800, height: 260},
      ],
      upcase: [
        {fontSize: 8, width: 136, height: 9},
        {fontSize: 288, width: 4920, height: 211},
      ],
      space: [
        {fontSize: 8, width: 4, height: 9},
        {fontSize: 288, width: 146, height: 200},
      ],
    },
  };

  public estimateTextWidth(text: string, fontFamily: string, fontSize: number): number {
    const textWidth = TextUtils.AVAILABLE_TYPES.reduce(
      (sum, type) =>
        sum + this.characterCountBy(type, text) * this.approximateCharacterLength(fontFamily, fontSize, type),
      0
    );

    return textWidth * TextUtils.INCREASE_WIDTH_RATE;
  }

  private characterCountBy(type: "upcase" | "downcase" | "space", text: string): number {
    switch (type) {
      case "downcase":
        return (text.match(/[a-z]/g) || []).length;
      case "upcase":
        return (text.match(/[A-Z]/g) || []).length;
      case "space":
        return (text.match(/[ .!?,:;]/g) || []).length;
    }
  }

  private approximateCharacterLength(
    fontFamily: string,
    fontSize: number,
    type: "upcase" | "downcase" | "space"
  ): number {
    const alphabetWidth = this.approximateWidthForAlphabet(fontFamily, fontSize, type);

    return alphabetWidth / (type === "space" ? 1 : TextUtils.ALPHABET_CHARACTER_LENGTH);
  }

  private approximateWidthForAlphabet(
    fontFamily: string,
    fontSize: number,
    type: "upcase" | "downcase" | "space"
  ): number {
    const data = TextUtils.FONT_DATA[fontFamily];

    if (!data) {
      throw new Error(`Font family '${fontFamily}' not found in FONT_DATA`);
    }

    const typeData = data[type];

    if (fontSize <= typeData[0].fontSize) return typeData[0].width;
    if (fontSize >= typeData[typeData.length - 1].fontSize) return typeData[typeData.length - 1].width;

    for (let i = 0; i < typeData.length - 1; i++) {
      const point1 = typeData[i];
      const point2 = typeData[i + 1];

      if (fontSize >= point1.fontSize && fontSize <= point2.fontSize) {
        return this.interpolateValue(point1.fontSize, point1.width, point2.fontSize, point2.width, fontSize);
      }
    }

    throw new Error(`Unable to approximate width for font size ${fontSize}`);
  }

  private interpolateValue(x0: number, y0: number, x1: number, y1: number, x: number): number {
    return ((y1 - y0) / (x1 - x0)) * (x - x0) + y0;
  }
}
/* v8 ignore stop */
